import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle2, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DocUploadPortal() {
  const { token } = useParams();
  const fileRef = useRef(null);
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [docType, setDocType] = useState("other");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploaded, setUploaded] = useState([]);

  useEffect(() => {
    axios.get(`${API}/doc-portal/${token}`)
      .then(({ data }) => { setInfo(data); setUploaded(data.uploaded || []); })
      .catch(() => setInvalid(true));
  }, [token]);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Choose a file first."); return; }
    if (docType === "other" && !label.trim()) { toast.error("Add a short label (e.g. Proof of address)."); return; }
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("label", label);
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/doc-portal/${token}/upload`, fd);
      toast.success(`${data.label} uploaded — thank you!`);
      setUploaded((u) => [...u, { label: data.label, at: new Date().toISOString() }]);
      if (fileRef.current) fileRef.current.value = "";
      setLabel("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed — please try again."); }
    finally { setBusy(false); }
  };

  if (invalid) return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <p className="text-white/60 text-sm" data-testid="doc-portal-invalid">This upload link is not valid. Please contact AutoHire STL for a new one.</p>
    </div>
  );
  if (!info) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <Loader2 className="animate-spin text-[#F2B705]" size={28} />
    </div>
  );
  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full" data-testid="doc-upload-portal">
        <h1 className="font-display font-black text-2xl text-white">Hi {info.first_name} 👋</h1>
        <p className="text-white/60 text-sm mt-2">AutoHire STL has requested documents from you. Upload them securely below — photos (JPG/PNG/HEIC) or PDF, up to 15MB each.</p>
        <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
          <label className="block text-[11px] text-white/45 uppercase tracking-wider">Document type
            <select data-testid="portal-doc-type" value={docType} onChange={(e) => setDocType(e.target.value)}
              className="block w-full mt-1.5 bg-black border border-white/15 rounded-md px-3 py-2.5 text-white text-sm normal-case">
              {Object.entries(info.doc_types).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          {docType === "other" && (
            <label className="block text-[11px] text-white/45 uppercase tracking-wider">What is this document?
              <input data-testid="portal-doc-label" value={label} onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Proof of address, pay stub, utility bill"
                className="block w-full mt-1.5 bg-black border border-white/15 rounded-md px-3 py-2.5 text-white text-sm normal-case" />
            </label>
          )}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,application/pdf" data-testid="portal-doc-file"
            className="block w-full text-sm text-white/60 file:mr-3 file:px-3 file:py-2 file:rounded-md file:border-0 file:bg-white/10 file:text-white/80 file:text-sm file:cursor-pointer" />
          <button data-testid="portal-upload-btn" onClick={upload} disabled={busy}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-[#F2B705] text-black font-bold text-sm hover:bg-white transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload Document
          </button>
        </div>
        {uploaded.length > 0 && (
          <div className="mt-5" data-testid="portal-uploaded-list">
            <p className="text-[11px] text-white/40 uppercase tracking-wider mb-2">Received</p>
            {uploaded.map((u, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-emerald-300 py-0.5">
                <CheckCircle2 size={14} /> {u.label}
              </div>
            ))}
            <p className="text-white/40 text-xs mt-3 flex items-center gap-1.5"><FileText size={12} /> You can upload as many documents as needed, then simply close this page.</p>
          </div>
        )}
      </div>
    </div>
  );
}
